import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import TutorialButton from './common/TutorialButton'; // Import TutorialButton
import { TabulatorFull as Tabulator } from 'tabulator-tables';
import api from '../services/api';
import { tratarErroAPI, logarRequisicao, logarSucesso } from '../utils/errorHandler';
import '../styles/status.css';
import etgLogo from '../assets/etg.png';
import ufmgLogo from '../assets/Ufmg-sem nome-03.png';
import '../styles/home.css';

const StatusManager = () => {
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mensagem, setMensagem] = useState({ tipo: '', texto: '' });
  const [formData, setFormData] = useState({
    id_status: '',
    tipo_status: '',
    descr_status: '',
    cor: '#000000',
  });
  const [apiStatus, setApiStatus] = useState('checking');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [proximoId, setProximoId] = useState('');
  const [camposComErro, setCamposComErro] = useState([]);

  const tabulatorRef = useRef(null);
  const tableRef = useRef(null);

  const mostrarMensagem = useCallback((tipo, texto) => {
    setMensagem({ tipo, texto });
    setTimeout(() => setMensagem({ tipo: '', texto: '' }), 4000);
  }, []);

  const verificarAPI = useCallback(async () => {
    try {
      setApiStatus('checking');
      await api.get('/status');
      setApiStatus('connected');
    } catch (error) {
      setApiStatus('error');
      mostrarMensagem('error', `Erro de conexão: ${error.message}`);
    }
  }, [mostrarMensagem]);



  // ID é gerado automaticamente no backend ao clicar em Adicionar
  const fetchNextId = useCallback(() => {
    // Não busca mais o próximo ID - será gerado atomicamente no backend
  }, []);

  useEffect(() => {
    verificarAPI();
    fetchNextId();
  }, [verificarAPI, fetchNextId]);

  const atualizarRegistro = useCallback(async (id, dadosAtualizacao) => {
    logarRequisicao('Atualizar', 'status', { id, dadosAtualizacao });
    try {
      const response = await api.put(`/status/${id}`, dadosAtualizacao);
      logarSucesso('Atualizar', 'status', response.data);
      if (response && response.data) {
        if (tabulatorRef.current) {
          tabulatorRef.current.updateData([response.data.data]);
        }
        mostrarMensagem('success', 'Registro atualizado com sucesso!');
      } else {
        mostrarMensagem('error', 'Resposta da API inválida ao atualizar.');
      }
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'atualizar', 'status');
      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem]);

  const excluirRegistro = useCallback(async (id) => {
    logarRequisicao('Excluir', 'status', { id });
    try {
      const response = await api.delete(`/status/${id}`);
      logarSucesso('Excluir', 'status', response.data);
      setDados(currentData => currentData.filter(item => item.id_status !== id));
      mostrarMensagem('success', 'Registro excluído com sucesso!');
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'excluir', 'status');
      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem]);

  const colunas = useMemo(() => [
    {
      title: 'Salvar',
      field: 'salvar',
      width: 80,
      hozAlign: 'center',
      formatter: () => '<button class="btn-save"><i class="fas fa-save"></i></button>',
      cellClick: (e, cell) => {
        const data = cell.getRow().getData();
        atualizarRegistro(data.id_status, data);
      },
      headerSort: false
    },
    {
      title: 'Excluir',
      field: 'excluir',
      width: 80,
      hozAlign: 'center',
      formatter: () => '<button class="btn-delete"><i class="fas fa-trash"></i></button>',
      cellClick: (e, cell) => {
        const data = cell.getRow().getData();
        if (window.confirm(`Deseja realmente excluir o registro "${data.id_status}"?`)) {
          excluirRegistro(data.id_status);
        }
      },
      headerSort: false
    },
    {
      title: 'ID',
      field: 'id_status',
      width: 150,
      headerFilter: 'input',
      headerFilterPlaceholder: 'Filtrar ID...',
      sorter: 'string',
      editor: 'input'
    },
    {
      title: 'Tipo',
      field: 'tipo_status',
      headerFilter: 'input',
      headerFilterPlaceholder: 'Filtrar tipo...',
      sorter: 'string',
      editor: 'input'
    },
    {
      title: 'Descrição',
      field: 'descr_status',
      headerFilter: 'input',
      headerFilterPlaceholder: 'Filtrar descrição...',
      sorter: 'string',
      editor: 'input'
    },
    {
      title: "Cor",
      field: "cor",
      width: 150,
      hozAlign: "center",
      editor: "input",
      headerFilter: "input",
      formatter: function (cell, formatterParams, onRendered) {
        const color = cell.getValue();
        if (!color) return '';

        // Função para determinar a cor do texto (preto ou branco) com base na cor de fundo
        const getTextColor = (bgColor) => {
          if (!bgColor) return '#000000';
          const color = (bgColor.charAt(0) === '#') ? bgColor.substring(1, 7) : bgColor;
          const r = parseInt(color.substring(0, 2), 16); // Red
          const g = parseInt(color.substring(2, 4), 16); // Green
          const b = parseInt(color.substring(4, 6), 16); // Blue
          const brightness = ((r * 299) + (g * 587) + (b * 114)) / 1000;
          return brightness > 155 ? '#000000' : '#FFFFFF';
        };

        const textColor = getTextColor(color);
        const span = document.createElement('span');
        span.style.backgroundColor = color;
        span.style.color = textColor;
        span.style.padding = '2px 8px';
        span.style.borderRadius = '12px';
        span.style.fontWeight = 'bold';
        span.style.display = 'inline-block';
        span.textContent = cell.getRow().getData().tipo_status || color;

        return span;
      },
    },
  ], [atualizarRegistro, excluirRegistro]);

  const carregarDados = useCallback(async (showMessage = true) => {
    try {
      setLoading(true);
      const response = await api.get('/status');
      setDados(response.data.data || []);
      setLastUpdate(new Date());
      if (showMessage) {
        mostrarMensagem('success', `${response.data.data?.length || 0} registros carregados`);
      }
    } catch (error) {
      mostrarMensagem('error', error.message);
      setDados([]);
    } finally {
      setLoading(false);
    }
  }, [mostrarMensagem]);

  useEffect(() => {
    if (apiStatus === 'connected') {
      carregarDados(false);
    }
  }, [apiStatus, carregarDados]);

  const inicializarTabulator = useCallback(() => {
    if (!tableRef.current) return;
    if (tabulatorRef.current) tabulatorRef.current.destroy();

    tabulatorRef.current = new Tabulator(tableRef.current, {
      data: dados,
      columns: colunas,
      layout: 'fitData',
      pagination: 'local',
      paginationSize: 10,
      paginationCounter: 'rows',
      movableColumns: true,
      resizableRows: true,
      tooltips: true,
      placeholder: 'Nenhum registro encontrado',
      index: 'id_status'
    });

    const style = document.createElement('style');
    style.textContent = `
      .btn-save {
        background: #007bff;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-save:hover {
        background: #0056b3;
      }
      .btn-delete {
        background: #dc3545;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-delete:hover {
        background: #c82333;
      }
      .btn-back {
        background: transparent;
        border: none;
        font-size: 24px;
        cursor: pointer;
      }
    `;
    document.head.appendChild(style);
  }, [dados, colunas]);

  useEffect(() => {
    if (apiStatus === 'connected' && tableRef.current) {
      if (!tabulatorRef.current) {
        inicializarTabulator();
      } else {
        tabulatorRef.current.setData(dados);
      }
    }
  }, [dados, apiStatus, inicializarTabulator]);

  const adicionarRegistro = async (e) => {
    e.preventDefault();
    const { tipo_status, descr_status, cor } = formData;

    const camposFaltando = [];
    if (!tipo_status) camposFaltando.push('Tipo');
    if (!descr_status) camposFaltando.push('Descrição');
    if (!cor) camposFaltando.push('Cor');

    if (camposFaltando.length > 0) {
      mostrarMensagem('error', `Por favor, preencha os campos obrigatórios: ${camposFaltando.join(', ')}`);
      return;
    }

    const dataToSend = {
      id_status: 'AUTO',
      tipo_status,
      descr_status,
      cor,
    };

    logarRequisicao('Adicionar', 'status', dataToSend);
    try {
      setLoading(true);
      const response = await api.post('/status', dataToSend);
      logarSucesso('Adicionar', 'status', response.data);
      setDados([...dados, response.data.data]);
      setFormData({ id_status: '', tipo_status: '', descr_status: '', cor: '#000000' });

      setCamposComErro([]);
      mostrarMensagem('success', 'Registro adicionado com sucesso!');
      fetchNextId();
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'adicionar', 'status');
      mostrarMensagem('error', mensagemErro);
    }
    finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const exportarDados = () => {
    if (tabulatorRef.current) {
      tabulatorRef.current.download('csv', 'status.csv');
    }
  };

  return (
    <div className="tipo-ambiente-container">
      <div className="header-controls">
        <button className="btn-back" onClick={() => window.history.back()}><i className="fas fa-arrow-left"></i></button>
      </div>

      <div className="header">
        <div className="title-container">
          <img src={etgLogo} alt="ETG Logo" className="logo" />
          <h1>Gerenciador de Status</h1>
          <img src={ufmgLogo} alt="UFMG Logo" className="logo" />
        </div>
        <p>Interface para popular e gerenciar a tabela tb_status</p>
        <div style={{ marginTop: '10px' }}>
          <TutorialButton videoPath="bdados/status.mov" buttonText="Ver Tutorial de Status" />
        </div>
      </div>

      {mensagem.texto && (
        <div className="status-container">
          <div className={`status ${mensagem.tipo}`}>{mensagem.texto}</div>
        </div>
      )}

      <div className="controls">
        <form onSubmit={adicionarRegistro} className="form-inline">
          <div className="form-group">
            <label htmlFor="id_status">ID:</label>
            <input type="text" id="id_status" name="id_status" value="" placeholder="Gerado ao adicionar" maxLength={50} disabled={true} style={{ backgroundColor: '#e9e9e9', cursor: 'not-allowed', color: '#888' }} />
          </div>
          <div className="form-group">
            <label htmlFor="tipo_status">Tipo:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input type="text" id="tipo_status" name="tipo_status" value={formData.tipo_status} onChange={handleInputChange} placeholder="Ex: Aberto" maxLength={50} disabled={loading || apiStatus !== 'connected'} />
          </div>
          <div className="form-group">
            <label htmlFor="descr_status">Descrição:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input type="text" id="descr_status" name="descr_status" value={formData.descr_status} onChange={handleInputChange} placeholder="Ex: Solicitação aberta" maxLength={100} disabled={loading || apiStatus !== 'connected'} />
          </div>
          <div className="form-group">
            <label htmlFor="cor">Cor:</label>
            <input type="color" id="cor" name="cor" value={formData.cor || "#000000"} onChange={handleInputChange} disabled={loading || apiStatus !== 'connected'} />
          </div>
          {/* Linha única: Adicionar, Recarregar, Exportar CSV + aviso de obrigatórios */}
          <div
            className="actions-row"
            style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'nowrap', marginTop: '10px' }}
          >
            <button type="submit" className="btn btn-success" disabled={loading || apiStatus !== 'connected'}>
              {loading ? '⏳ Adicionando...' : '➕ Adicionar'}
            </button>
            <button className="btn btn-primary" onClick={() => carregarDados(true)} disabled={loading || apiStatus !== 'connected'}>
              {loading ? '⏳ Carregando...' : '🔄 Recarregar'}
            </button>
            <button className="btn btn-secondary" onClick={exportarDados} disabled={dados.length === 0}>📥 Exportar CSV</button>

            <div className="info-campos-obrigatorios" style={{ whiteSpace: 'nowrap' }}>
              <i className="fas fa-info-circle"></i>
              <span style={{ marginLeft: 6 }}>* indica que os campos são obrigatorios.</span>
            </div>
          </div>
        </form>
      </div>

      <div className="stats-bar">
        <div className="stats-item"><strong>{dados.length}</strong> registros carregados</div>
        <div className="stats-item">Última atualização: <strong>{lastUpdate ? lastUpdate.toLocaleTimeString() : 'N/A'}</strong></div>
      </div>

      <div className="tabulator-container">
        <div ref={tableRef}></div>
      </div>

    </div>
  );
};

export default StatusManager;